// SPDX-License-Identifier: AGPL-3.0-or-later
// SPDX-FileCopyrightText: 2026 Dobrev IT Ltd

import { useEffect, useRef, useState } from "react";
import { Button, Dropdown, Input, Popover, Space, Tooltip, type GetRef } from "antd";
import { AudioOutlined, BarChartOutlined, BoldOutlined, CloseOutlined, CodeOutlined, DeleteOutlined, ItalicOutlined, PaperClipOutlined, PlusOutlined, SendOutlined, SmileOutlined, StrikethroughOutlined } from "@ant-design/icons";
import { EmojiPicker } from "./EmojiPicker";

// fmtSecs renders an elapsed recording time as m:ss.
function fmtSecs(s: number): string {
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
}

// Composer is the message input. It supports IRC slash commands (parsed by the
// caller), shows a reply/edit context banner, can be prefilled (edit), emits
// typing notices while the user writes, and offers a poll-creation button.
export function Composer({
  disabled,
  onSubmit,
  onTyping,
  onPoll,
  onAttach,
  onVoice,
  draft,
  banner,
}: {
  readonly disabled?: boolean;
  readonly onSubmit: (line: string) => void;
  readonly onTyping?: (typing: boolean) => void;
  readonly onPoll?: () => void;
  readonly onAttach?: (file: File) => void;
  readonly onVoice?: (blob: Blob, durationMs: number) => void;
  readonly draft?: { text: string; seq: number };
  readonly banner?: { label: string; text: string; onCancel: () => void };
}) {
  const [value, setValue] = useState("");
  const isCommand = value.trimStart().startsWith("/");
  const isTouch = typeof matchMedia !== "undefined" && matchMedia("(pointer: coarse)").matches;
  const lastSent = useRef(0);
  const idle = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const fileRef = useRef<HTMLInputElement>(null);
  const taRef = useRef<GetRef<typeof Input.TextArea>>(null);

  // surround wraps the current selection (or the cursor) with markdown markers — the
  // formatting toolbar's bold/italic/code/etc., for users who don't know the shortcuts.
  const surround = (before: string, after = before) => {
    const ta = taRef.current?.resizableTextArea?.textArea;
    const s = ta?.selectionStart ?? value.length;
    const e = ta?.selectionEnd ?? value.length;
    const next = value.slice(0, s) + before + value.slice(s, e) + after + value.slice(e);
    setValue(next);
    requestAnimationFrame(() => {
      ta?.focus();
      ta?.setSelectionRange(s + before.length, e + before.length); // keep the text selected, inside the markers
    });
  };

  // ---- voice messages (record → onVoice) ----
  const [recording, setRecording] = useState(false);
  const [recSecs, setRecSecs] = useState(0);
  const recorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const recTimer = useRef<ReturnType<typeof setInterval> | undefined>(undefined);
  const recStart = useRef(0);
  const cancelled = useRef(false);

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      chunksRef.current = [];
      cancelled.current = false;
      const rec = new MediaRecorder(stream);
      recorderRef.current = rec;
      rec.ondataavailable = (e) => {
        if (e.data.size) chunksRef.current.push(e.data);
      };
      rec.onstop = () => {
        stream.getTracks().forEach((t) => t.stop());
        const durationMs = Date.now() - recStart.current;
        if (!cancelled.current && chunksRef.current.length) {
          onVoice?.(new Blob(chunksRef.current, { type: rec.mimeType || "audio/webm" }), durationMs);
        }
      };
      rec.start();
      recStart.current = Date.now();
      setRecSecs(0);
      setRecording(true);
      recTimer.current = setInterval(() => setRecSecs((s) => s + 1), 1000);
    } catch {
      /* mic denied / unavailable — stay in text mode */
    }
  };
  const stopRecording = (send: boolean) => {
    cancelled.current = !send;
    if (recTimer.current) clearInterval(recTimer.current);
    recorderRef.current?.stop();
    setRecording(false);
  };
  // Tear down a live recording if the composer unmounts mid-record.
  useEffect(() => {
    return () => {
      if (recTimer.current) clearInterval(recTimer.current);
      if (recorderRef.current && recorderRef.current.state !== "inactive") {
        cancelled.current = true;
        recorderRef.current.stop();
      }
    };
  }, []);

  // Prefill the input when the draft nonce changes (e.g. starting an edit).
  useEffect(() => {
    if (draft && draft.seq > 0) setValue(draft.text);
  }, [draft?.seq]); // eslint-disable-line react-hooks/exhaustive-deps

  function stopTyping() {
    if (idle.current) clearTimeout(idle.current);
    if (lastSent.current) {
      lastSent.current = 0;
      onTyping?.(false);
    }
  }
  // Refresh "typing" at most every 3s; auto-stop 4s after the last keystroke.
  function fireTyping() {
    if (!onTyping) return;
    const now = Date.now();
    if (now - lastSent.current > 3000) {
      lastSent.current = now;
      onTyping(true);
    }
    if (idle.current) clearTimeout(idle.current);
    idle.current = setTimeout(stopTyping, 4000);
  }

  function change(next: string) {
    setValue(next);
    if (next && !next.startsWith("/")) fireTyping();
    else stopTyping();
  }

  function submit() {
    const line = value.trim();
    if (!line) return;
    stopTyping();
    onSubmit(line);
    setValue("");
  }

  return (
    <div className="composer">
      {banner && (
        <div className="composer-banner">
          <span className="composer-banner-label">{banner.label}:</span>
          <span className="composer-banner-text">{banner.text}</span>
          <Button type="text" size="small" icon={<CloseOutlined />} onClick={banner.onCancel} title="Cancel" />
        </div>
      )}
      {recording ? (
        <div className="composer-recording">
          <span className="rec-dot" />
          <span className="rec-time">{fmtSecs(recSecs)}</span>
          <span className="rec-hint">Recording voice message…</span>
          <Button size="large" icon={<DeleteOutlined />} onClick={() => stopRecording(false)} title="Cancel" />
          <Button size="large" type="primary" icon={<SendOutlined />} onClick={() => stopRecording(true)} title="Send voice message" />
        </div>
      ) : (
      <>
        {!isCommand && (
          <div className="composer-format">
            <Button type="text" size="small" icon={<BoldOutlined />} onClick={() => surround("**")} disabled={disabled} title="Bold  **text**" />
            <Button type="text" size="small" icon={<ItalicOutlined />} onClick={() => surround("_")} disabled={disabled} title="Italic  _text_" />
            <Button type="text" size="small" icon={<StrikethroughOutlined />} onClick={() => surround("~~")} disabled={disabled} title="Strikethrough  ~~text~~" />
            <Button type="text" size="small" icon={<CodeOutlined />} onClick={() => surround("`")} disabled={disabled} title="Inline code  `code`" />
            <Button type="text" size="small" className="composer-format-block" onClick={() => surround("```\n", "\n```")} disabled={disabled} title="Code block">
              {"```"}
            </Button>
          </div>
        )}
      <Space.Compact style={{ width: "100%" }}>
        {onAttach && (
          <>
            <input
              ref={fileRef}
              type="file"
              className="composer-file"
              style={{ display: "none" }}
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) onAttach(f);
                e.target.value = "";
              }}
            />
            <Button
              size="large"
              className="composer-attach-full"
              icon={<PaperClipOutlined />}
              onClick={() => fileRef.current?.click()}
              disabled={disabled}
              title="Attach a file"
            />
          </>
        )}
        {onPoll && (
          <Button
            size="large"
            className="composer-poll-full"
            icon={<BarChartOutlined />}
            onClick={onPoll}
            disabled={disabled}
            title="Create a poll"
          />
        )}
        {(onAttach || onPoll) && (
          <Dropdown
            trigger={["click"]}
            placement="topLeft"
            menu={{
              items: [
                ...(onAttach ? [{ key: "attach", label: "Attach file", onClick: () => fileRef.current?.click() }] : []),
                ...(onPoll ? [{ key: "poll", label: "Create a poll", onClick: onPoll }] : []),
              ],
            }}
          >
            <Button size="large" className="composer-plus" icon={<PlusOutlined />} disabled={disabled} title="Add" />
          </Dropdown>
        )}
        <Input.TextArea
          ref={taRef}
          size="large"
          value={value}
          disabled={disabled}
          autoSize={{ minRows: 1, maxRows: 8 }}
          onChange={(e) => change(e.target.value)}
          onKeyDown={(e) => {
            // Desktop: Enter sends, Ctrl/Alt/Shift+Enter inserts a newline. Touch: Enter
            // always inserts a newline (you send with the button) — soft keyboards + Enter
            // shouldn't fire off half-typed messages.
            if (e.key === "Enter" && !isTouch && !e.shiftKey && !e.ctrlKey && !e.altKey && !e.metaKey) {
              e.preventDefault();
              submit();
            }
          }}
          onBlur={stopTyping}
          placeholder={disabled ? "Join a channel to chat…" : "Message — **bold**, `code`, ```blocks```; /help for commands"}
          // A dynamic prefix/suffix makes antd's Input lose focus on the structure
          // change (so Enter wouldn't submit). Indicate command mode by class only.
          className={isCommand ? "is-command composer-textarea" : "composer-textarea"}
        />
        <Popover
          trigger="click"
          placement="topRight"
          content={<EmojiPicker onPick={(e) => setValue((v) => v + e)} />}
        >
          {/* Desktop only — on phones the OS keyboard already has emoji (hidden via CSS). */}
          <Button size="large" className="composer-emoji-full" icon={<SmileOutlined />} disabled={disabled} title="Emoji" />
        </Popover>
        {onVoice && !value.trim() ? (
          <Tooltip title="Record a voice message">
            <Button size="large" icon={<AudioOutlined />} onClick={startRecording} disabled={disabled} />
          </Tooltip>
        ) : (
          <Tooltip title="Send (Enter)">
            <Button size="large" icon={<SendOutlined />} onClick={submit} disabled={disabled} />
          </Tooltip>
        )}
      </Space.Compact>
      </>
      )}
    </div>
  );
}
